import React, { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArrowLeft,
  Boxes,
  Download,
  Edit3,
  IndianRupee,
  MoreVertical,
  PackageCheck,
  Share2,
  Trash2,
} from "lucide-react";
import { AES, enc } from "crypto-js";
import JsBarcode from "react-barcode";
import swal from "sweetalert";
import PremiumListPage, { StatusBadge } from "../../../Components/Dashboard/PremiumListPage";
import SearchBar from "../../../Components/Common/SearchBar";
import ItemUpdate from "./ItemUpdate";
import { BASE_URL } from "../CommonApi";
import "../Sales/ModernInvoiceWorkspace.css";

const decodeUniqueId = () => AES.decrypt(localStorage.getItem("uniqueId1"), "1234567812345678").toString(enc.Utf8);

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const money = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const valueOrDash = (value) => (value === null || value === undefined || value === "" ? "-" : value);
const initials = (name = "") => String(name || "IT").slice(0, 2).toUpperCase();

function Items({ navigation }) {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editItemId, setEditItemId] = useState("");
  const [showWorkspace, setShowWorkspace] = useState(false);

  const jwtToken = localStorage.getItem("jwtToken");

  const fetchItems = () => {
    setLoading(true);
    fetch(BASE_URL + "/getAllItemData", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId(), vendorId: "" }),
    })
      .then((response) => response.json())
      .then((data) => {
        const nextRows = data.status === "success" ? normalizeRows(data.responseMessage) : [];
        setItems(nextRows);
        setSelectedItemId((current) => current || nextRows[0]?.id || "");
        if (!nextRows.length) setSelectedItem({});
      })
      .catch((error) => console.error("Error fetching items:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    if (!selectedItemId) return;
    setDetailLoading(true);
    fetch(BASE_URL + `/getItemData/${selectedItemId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setSelectedItem(data.data || items.find((item) => item.id === selectedItemId) || {});
      })
      .catch((error) => {
        console.error("Error fetching item detail:", error);
        setSelectedItem(items.find((item) => item.id === selectedItemId) || {});
      })
      .finally(() => setDetailLoading(false));
  }, [selectedItemId]);

  const itemType = (item = {}) => {
    const type = String(item.type || "").toLowerCase();
    if (type.includes("service")) return "service";
    if (type.includes("inventory")) return "inventory";
    return "item";
  };

  const counts = useMemo(
    () => ({
      all: items.length,
      inventory: items.filter((item) => itemType(item) === "inventory").length,
      service: items.filter((item) => itemType(item) === "service").length,
      low: items.filter((item) => Number(item.openingStock || 0) <= Number(item.reorderPoint || -1)).length,
    }),
    [items],
  );

  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesType = typeFilter === "all" || (typeFilter === "low" ? Number(item.openingStock || 0) <= Number(item.reorderPoint || -1) : itemType(item) === typeFilter);
      const matchesSearch =
        !search ||
        [item.name, item.type, item.unit, item.salesPrice, item.purchasePrice, item.openingStock]
          .some((value) => String(value || "").toLowerCase().includes(search));
      return matchesType && matchesSearch;
    });
  }, [items, query, typeFilter]);

  const deleteItem = (item) => {
    if (!window.confirm("Are you sure? Once deleted, you will not be able to recover this item!")) return;
    fetch(BASE_URL + `/deleteItem/${item.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId() }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          setItems((current) => current.filter((row) => row.id !== item.id));
          setSelectedItemId((current) => (current === item.id ? "" : current));
          swal({ icon: "success", title: "Item Deleted Successfully" });
        } else if (data.responseMessage) {
          swal({ icon: "error", title: data.responseMessage });
        }
      })
      .catch((error) => console.error("Error deleting item:", error));
  };

  const shareItem = () => {
    const text = `${selectedItem.name || "Item"} - ${money(selectedItem.salesPrice)} (${valueOrDash(selectedItem.openingStock)} in stock)`;
    if (navigator.share) {
      navigator.share({ title: selectedItem.name || "Item", text }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      swal({ icon: "success", title: "Item details copied" });
    }
  };

  const openItemWorkspace = (item) => {
    setSelectedItemId(item.id);
    setShowWorkspace(true);
  };

  if (editItemId) {
    return (
      <ItemUpdate
        id={editItemId}
        handleCancle={() => {
          setEditItemId("");
          fetchItems();
        }}
      />
    );
  }

  if (!showWorkspace) {
    if (loading) return <div className="premium-empty-state">Loading items...</div>;

    const stock = items.reduce((sum, item) => sum + Number(item.openingStock || 0), 0);
    const inventoryItems = items.filter((item) => itemType(item) === "inventory").length;
    const salesValue = items.reduce((sum, item) => sum + Number(item.salesPrice || 0), 0);

    return (
      <PremiumListPage
        title="Items"
        subtitle="Manage products, services, pricing, and available stock."
        actionLabel="+ New Item"
        onCreate={() => navigation.navigate("ItemsNew")}
        searchPlaceholder="Search items..."
        rows={items.map((item, index) => ({ ...item, serial: index + 1 }))}
        stats={[
          { label: "Total Items", value: items.length, icon: Boxes, tone: "blue" },
          { label: "Inventory Items", value: inventoryItems, icon: PackageCheck, tone: "green" },
          { label: "Available Stock", value: stock, icon: Archive, tone: "orange" },
          { label: "Sales Value", value: money(salesValue), icon: IndianRupee, tone: "violet" },
        ]}
        columns={[
          { key: "serial", label: "Serial No." },
          {
            key: "name",
            label: "Name",
            render: (row) => (
              <button className="premium-link" onClick={() => openItemWorkspace(row)}>
                {row.name || "-"}
              </button>
            ),
          },
          { key: "salesPrice", label: "Sales Price", className: "premium-money", render: (row) => money(row.salesPrice) },
          { key: "purchasePrice", label: "Purchase Price", className: "premium-money", render: (row) => money(row.purchasePrice) },
          { key: "unit", label: "Unit" },
          { key: "type", label: "Type", render: (row) => <StatusBadge tone="blue">{row.type || "Item"}</StatusBadge> },
          { key: "openingStock", label: "Available Stock" },
        ]}
        onView={openItemWorkspace}
        onEdit={(row) => setEditItemId(row.id)}
        onDelete={deleteItem}
      />
    );
  }

  return (
    <section className="invoice-workspace item-workspace">
      <aside className="invoice-list-panel">
        <div className="invoice-list-head">
          <button className="invoice-back-btn" onClick={() => setShowWorkspace(false)} aria-label="Back to item list">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1>Items</h1>
            <p>{items.length} items</p>
          </div>
        </div>

        <div className="invoice-list-tools">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Search items..."
            className="invoice-list-search"
            ariaLabel="Search items"
          />
        </div>

        <div className="invoice-status-tabs">
          {[
            ["all", "All"],
            ["inventory", "Inventory"],
            ["service", "Services"],
            ["low", "Low Stock"],
          ].map(([key, label]) => (
            <button key={key} className={typeFilter === key ? "is-active" : ""} onClick={() => setTypeFilter(key)}>
              {label} <span>{counts[key]}</span>
            </button>
          ))}
        </div>

        <div className="invoice-list-scroll">
          {loading && <div className="invoice-empty">Loading items...</div>}
          {!loading && filteredItems.length === 0 && <div className="invoice-empty">No items found.</div>}
          {filteredItems.map((item) => {
            const active = item.id === selectedItemId;
            const tone = itemType(item) === "service" ? "pending" : "paid";
            return (
              <button
                key={item.id}
                className={`invoice-card-row ${active ? "is-active" : ""}`}
                onClick={() => setSelectedItemId(item.id)}
              >
                <span className={`invoice-avatar ${tone}`}>{initials(item.name)}</span>
                <span className="invoice-row-main">
                  <strong>{valueOrDash(item.name)}</strong>
                  <small>{valueOrDash(item.type || "Item")} • Stock {valueOrDash(item.openingStock || 0)}</small>
                  <b>{money(item.salesPrice)}</b>
                </span>
                <span className="invoice-row-side">
                  <time>{valueOrDash(item.unit, "Unit")}</time>
                  <span className={`invoice-pill ${tone}`}>{valueOrDash(item.type || "Item")}</span>
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <main className="invoice-detail-panel">
        {detailLoading && <div className="invoice-detail-loading">Loading item detail...</div>}
        {!detailLoading && !selectedItem.id && <div className="invoice-detail-loading">Select an item to preview.</div>}
        {!detailLoading && selectedItem.id && (
          <>
            <div className="invoice-detail-top">
              <div>
                <div className="invoice-title-row">
                  <h2>{valueOrDash(selectedItem.name)}</h2>
                  <span className={`invoice-pill ${itemType(selectedItem) === "service" ? "pending" : "paid"}`}>
                    {valueOrDash(selectedItem.type || "Item")}
                  </span>
                </div>
                <p>
                  Unit <strong>{valueOrDash(selectedItem.unit)}</strong> · Stock <strong>{valueOrDash(selectedItem.openingStock || 0)}</strong>
                </p>
              </div>
              <div className="invoice-actions">
                <button onClick={() => setEditItemId(selectedItem.id)}><Edit3 size={16} /> Edit</button>
                <button onClick={shareItem}><Share2 size={16} /> Share</button>
                <button onClick={() => deleteItem(selectedItem)}><Trash2 size={16} /> Delete</button>
                <button onClick={() => navigation.navigate("ItemsNew")}><Download size={16} /> New Item</button>
                <button aria-label="More"><MoreVertical size={16} /></button>
              </div>
            </div>

            <div className="invoice-payment-card paid">
              <PackageCheck size={22} />
              <div>
                <strong>Item is available in your catalog</strong>
                <span>
                  Stock {valueOrDash(selectedItem.openingStock || 0)} {valueOrDash(selectedItem.unit, "")} · Sales value {money(selectedItem.salesPrice)}
                </span>
              </div>
              <button onClick={() => setEditItemId(selectedItem.id)}>Update Item</button>
            </div>

            <div className="invoice-detail-grid">
              <section className="invoice-meta-stack">
                <article className="invoice-side-card">
                  <h3>Item Overview</h3>
                  <dl>
                    <div><dt>Item Type</dt><dd>{valueOrDash(selectedItem.type)}</dd></div>
                    <div><dt>Unit</dt><dd>{valueOrDash(selectedItem.unit)}</dd></div>
                    <div><dt>Tax Preference</dt><dd>{valueOrDash(selectedItem.taxPreference)}</dd></div>
                    <div><dt>HSN/SAC</dt><dd>{valueOrDash(selectedItem.hsnCode || selectedItem.hsnSac)}</dd></div>
                  </dl>
                </article>

                <article className="invoice-side-card">
                  <h3>Inventory</h3>
                  <dl>
                    <div><dt>Available Stock</dt><dd>{valueOrDash(selectedItem.openingStock || 0)}</dd></div>
                    <div><dt>Reorder Point</dt><dd>{valueOrDash(selectedItem.reorderPoint)}</dd></div>
                    <div><dt>Vendor</dt><dd>{valueOrDash(selectedItem.prefferedVendor || selectedItem.preferredVendor)}</dd></div>
                  </dl>
                </article>
              </section>

              <section className="invoice-preview-card item-preview-card">
                <div className="item-hero">
                  <span><Boxes size={28} /></span>
                  <div>
                    <h3>{valueOrDash(selectedItem.name)}</h3>
                    <p>{valueOrDash(selectedItem.description, "No sales description added.")}</p>
                  </div>
                </div>

                <div className="invoice-preview-meta">
                  <div><span>Sales Price</span><strong>{money(selectedItem.salesPrice)}</strong></div>
                  <div><span>Purchase Price</span><strong>{money(selectedItem.purchasePrice)}</strong></div>
                  <div><span>Stock</span><strong>{valueOrDash(selectedItem.openingStock || 0)}</strong></div>
                  <div><span>Unit</span><strong>{valueOrDash(selectedItem.unit)}</strong></div>
                </div>

                <div className="item-detail-grid">
                  <article>
                    <h4>Sales Information</h4>
                    <dl>
                      <div><dt>Sales Account</dt><dd>{valueOrDash(selectedItem.account)}</dd></div>
                      <div><dt>Description</dt><dd>{valueOrDash(selectedItem.description)}</dd></div>
                    </dl>
                  </article>
                  <article>
                    <h4>Purchase Information</h4>
                    <dl>
                      <div><dt>Purchase Account</dt><dd>{valueOrDash(selectedItem.accountP)}</dd></div>
                      <div><dt>Description</dt><dd>{valueOrDash(selectedItem.descriptionP)}</dd></div>
                    </dl>
                  </article>
                </div>

                <article className="item-barcode-card">
                  <div>
                    <h4>Barcode</h4>
                    <p>{valueOrDash(selectedItem.barcode || selectedItem.id)}</p>
                  </div>
                  <JsBarcode value={String(selectedItem.barcode || selectedItem.id || selectedItem.name)} width={1.4} height={42} displayValue={false} />
                </article>
              </section>
            </div>
          </>
        )}
      </main>
    </section>
  );
}

export default Items;
